<template>
	<div :class="slideInClass">
		<header>
			<div class="close-container">
				<a href="#" @click.prevent="close(false)">
					<Icon icon="close"/>
				</a>
			</div>
			<h4 v-html="computedTitle"/>
			<div v-if="withKeyword" class="characteristic-fan--search">
				<SearchInput
					v-model="keyword"
					:placeholder="keywordPlaceholder || 'Suche... (mind. 3 Zeichen)'"
					:characteristic-type="characteristicType"
					:catalog="searchConfig ? searchConfig.catalog : undefined"
				/>
			</div>
		</header>
		<main class="scrollbar-check">
			<div class="items">
				<CharacteristicItem
					v-if="isNothingAllowed"
					:characteristic="characteristic"
					:level="level"
					nothing
				/>
				<div
					v-for="itemGroup in childrenGroupedByLetter"
					:key="itemGroup.id"
				>
					<Separation
						v-if="itemGroup.separation"
						:separation="itemGroup.separation"
						class="items-separation"
					/>
					<div
						v-for="(characteristics, letter) in itemGroup.items"
						:key="letter"
						class="letter-section"
					>
						<div class="letter">{{ letter }}</div>
						<div class="items">
							<CharacteristicItem
								v-for="child in characteristics"
								:key="child.id"
								:characteristic="child"
								:level="level"
								:keyword="hasValidKeyword ? keyword : undefined"
							/>
						</div>
					</div>
				</div>
			</div>
		</main>
		<footer>
			<Button
				text="OK"
				icon="checkmark"
				:disabled="!valid"
				@click="close(true)"
			/>
		</footer>
	</div>
</template>

<script>
import CharacteristicItem from "@/vue/characteristic/fan/CharacteristicItem";
import Icon from "@/vue/icon/Icon";
import Button from "@/vue/common/Button";
import {mapActions, mapState} from "vuex";
import Label from "@/vue/characteristic/Label";
import {itemsValid} from "@/helper/characteristic/Edit";
import {getClasses} from "@/helper/layout/Fan";
import {get} from "@/helper/characteristic/Label";
import SearchInput from "@/vue/common/form/SearchInput.vue";
import {shorten} from "@/helper/characteristic/Shorten";
import {isEmptyOrNull, maxLength} from "@/helper/String";
import {hasSelectedChildsToLastLevel, isSelected} from "@/helper/characteristic/Characteristic";
import {group} from "@/helper/characteristic/GroupByLetter";
import {uuid} from "vue-uuid";
import Separation from "@/vue/characteristic/separation/Separation.vue";

export default {

	components: {
		Separation,
		SearchInput,
		Label,
		Button,
		Icon,
		CharacteristicItem,
	},

	props: {
		characteristic: {
			type: Object,
			required: true,
		},
		level: {
			type: Number,
			required: true,
		},
		initialKeyword: {
			type: String,
			required: false
		}
	},

	mounted ()
	{
		if (!isEmptyOrNull(this.keyword) && this.filteredCharacteristicItems.length === 0)
		{
			this.keyword = '';
		}
	},

	data ()
	{
		return {
			fanKey: 'characteristic-fan-parent-' + this.characteristic.id,
			keyword: this.initialKeyword || '',
		};
	},

	computed: {
		...mapState('characteristic/fan', [
			'characteristicType',
			'titleFormats',
			'selected',
			'parents',
			'nothingAllowed',
			'withExperience',
			'withSelfAssessment',
			'withEducation',
			'withRequirement',
			'supplementsConfig',
			'showMultipleHint',
			'groupByLetter',
			'withKeyword',
			'keywordPlaceholder',
			'searchConfig'
		]),
		...mapState('layout', ['fanLevelClasses']),
		slideInClass ()
		{
			return getClasses({
				fanLevelClasses: this.fanLevelClasses,
				key: this.fanKey,
				extraClasses: ['characteristic-fan slide-in']
			});
		},
		computedTitle ()
		{
			const characteristicLabel = get(this.characteristic);

			let title = characteristicLabel;

			if (this.titleFormats?.level && this.titleFormats.level[this.level])
			{
				title = this.titleFormats.level[this.level];
			}
			else if (this.titleFormats?.default)
			{
				title = this.titleFormats.default;
			}

			return (title || '').replaceAll('%characteristicLabel%', maxLength(characteristicLabel, 50));
		},
		filteredCharacteristicItems ()
		{
			if (this.hasValidKeyword)
			{
				return shorten(this.characteristic.children, this.keyword, this.searchConfig?.type);
			}

			return this.characteristic.children;
		},
		childrenGroupedByLetter ()
		{
			const items = this.filteredCharacteristicItems;

			if (!this.groupByLetter?.enabled)
			{
				return [{
					id: uuid.v4(),
					items: {
						'': items
					}
				}];
			}

			return group({items});
		},
		hasValidKeyword ()
		{
			return !isEmptyOrNull(this.keyword) && this.keyword.length >= 3;
		},
		valid ()
		{
			return (isSelected(this.selected, this.characteristic) || hasSelectedChildsToLastLevel(this.selected, this.characteristic))
				&& itemsValid({
					selected: this.selected,
					items: this.characteristic.children,
					withExperience: this.withExperience,
					withSelfAssessment: this.withSelfAssessment,
					withEducation: this.withEducation,
					withRequirement: this.withRequirement,
					supplementsConfig: this.supplementsConfig
				});
		},
		isNothingAllowed ()
		{
			return this.nothingAllowed
				&& this.nothingAllowed?.onlyOverall !== true
				&& this.level >= (this.nothingAllowed?.minimumLevel || 0);
		},
	},

	methods: {
		...mapActions('characteristic/fan', [
			'removeParent',
		]),
		close (confirmed)
		{
			this.keyword = '';

			this.removeParent({
				characteristic: this.characteristic,
				confirmed
			});
		},
	},
}
</script>
